import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Diamond, Sparkles, Percent, KeyRound, AlertTriangle } from "lucide-react";
import ReAuthModal from "../components/ReAuthModal";

const ROADMAP = [
  "Barkod etiketi yazdırma",
  "Niimbot etiket sistemi entegrasyonu",
  "Toplu Excel ile ürün yükleme",
  "Detaylı satış raporları",
  "Tedarikçi yönetimi",
  "Gider takibi",
  "Kâr analizi",
  "Günlük kasa kapanışı",
  "Mobil biometrik giriş (PIN / Face ID / parmak izi)",
];

export default function Settings() {
  const { user, isOwner } = useAuth();
  const [maxDiscount, setMaxDiscount] = useState("");
  const [pinIsDefault, setPinIsDefault] = useState(false);
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [pinReauthOpen, setPinReauthOpen] = useState(false);

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => {
        setMaxDiscount(String(r.data.cashier_max_discount_pct ?? 15));
        setPinIsDefault(!!r.data.admin_pin_is_default);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const saveDiscount = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", { cashier_max_discount_pct: Number(maxDiscount) });
      toast.success("Ayarlar kaydedildi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  };

  const savePin = async () => {
    if (!newPin || newPin.length < 6) return toast.error("Yeni PIN en az 6 karakter olmalı");
    if (newPin === "1234") return toast.error("Varsayılan PIN kullanılamaz");
    if (!/\d/.test(newPin)) return toast.error("PIN en az bir rakam içermeli");
    setPinReauthOpen(true);
  };

  const runPinChange = async (reauthToken) => {
    setSaving(true);
    try {
      const body = { new_admin_pin: newPin };
      if (!pinIsDefault) body.current_admin_pin = currentPin;
      await api.patch("/settings", body, { headers: { "X-Reauth-Token": reauthToken } });
      toast.success("PIN güncellendi — varsayılan PIN kilidi kaldırıldı");
      setCurrentPin("");
      setNewPin("");
      setPinIsDefault(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PIN güncellenemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-root" className="max-w-3xl">
      <div className="mb-6">
        <div className="font-mono-code text-[10px] tracking-[0.3em] text-stone-500">
          HESAP & POS
        </div>
        <h1 className="font-serif-display text-3xl sm:text-4xl text-stone-900">
          Ayarlar
        </h1>
      </div>

      <div className="card-lux p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl gold-gradient flex items-center justify-center">
            <Diamond className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-stone-500">
              {user?.role === "owner" ? "İşletme Sahibi" : "Yönetici"}
            </div>
            <div className="font-serif-display text-2xl text-stone-900">
              {user?.name}
            </div>
            <div className="text-sm text-stone-500">{user?.email || user?.username}</div>
          </div>
        </div>
      </div>

      <div className="card-lux p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Percent className="w-4 h-4 text-amber-600" />
          <h3 className="font-serif-display text-xl text-stone-900">
            Kasiyer İndirim Limiti
          </h3>
        </div>
        {loaded ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
            <label className="block">
              <div className="text-xs uppercase tracking-wider font-semibold text-stone-500 mb-1">
                Genel Maksimum İndirim (%)
              </div>
              <input
                data-testid="settings-max-discount"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={maxDiscount}
                onChange={(e) => setMaxDiscount(e.target.value)}
                className="w-full px-3 py-2 border border-stone-200 rounded-lg outline-none focus:border-amber-400"
              />
              <div className="text-[11px] text-stone-500 mt-1">
                Kullanıcı bazlı yetki verilirse o değer öncelikli olur.
              </div>
            </label>
            <button
              data-testid="settings-save"
              onClick={saveDiscount}
              disabled={saving}
              className="px-5 py-2 rounded-lg gold-gradient text-white text-sm disabled:opacity-60"
            >
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        ) : (
          <div className="text-stone-500 text-sm">Yükleniyor…</div>
        )}
      </div>

      {isOwner && loaded && (
        <div className="card-lux p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound className="w-4 h-4 text-amber-600" />
            <h3 className="font-serif-display text-xl text-stone-900">Yönetici PIN</h3>
          </div>
          {pinIsDefault && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                Şu anda varsayılan PIN kullanılıyor. Güvenlik için kendi PIN'inizi belirleyin.
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {!pinIsDefault && (
              <label className="block">
                <div className="text-xs uppercase tracking-wider font-semibold text-stone-500 mb-1">
                  Mevcut PIN
                </div>
                <input
                  data-testid="settings-current-pin"
                  type="password"
                  value={currentPin}
                  onChange={(e) => setCurrentPin(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-200 rounded-lg outline-none focus:border-amber-400 font-mono-code"
                />
              </label>
            )}
            <label className="block">
              <div className="text-xs uppercase tracking-wider font-semibold text-stone-500 mb-1">
                Yeni PIN (en az 6 karakter, rakam içermeli)
              </div>
              <input
                data-testid="settings-new-pin"
                type="password"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value)}
                className="w-full px-3 py-2 border border-stone-200 rounded-lg outline-none focus:border-amber-400 font-mono-code"
              />
            </label>
            <div className="sm:col-span-2 flex justify-end">
              <button
                data-testid="settings-save-pin"
                onClick={savePin}
                disabled={saving}
                className="px-5 py-2 rounded-lg gold-gradient text-white text-sm disabled:opacity-60"
              >
                {saving ? "Kaydediliyor..." : "PIN'i Güncelle"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ReAuthModal
        open={pinReauthOpen}
        scope="admin_pin"
        title="Yönetici PIN'i değiştirmek yüksek güvenlikli bir işlem — Owner şifresi ve Authenticator kodu gerekli."
        ownerHasTotp={!!user?.totp_enabled}
        onClose={() => setPinReauthOpen(false)}
        onSuccess={(tok) => { setPinReauthOpen(false); runPinChange(tok); }}
      />

      <div className="card-lux p-6">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-amber-600" />
          <h3 className="font-serif-display text-xl text-stone-900">
            Yakında gelecek modüller
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {ROADMAP.map((r) => (
            <div
              key={r}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-stone-200 bg-stone-50/50 text-sm text-stone-700"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              {r}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
